import Container from 'typedi';
import { LoggerProvider } from '../logger';
import { IPaymentAction } from '../payments/models';
import { IPostMessageHandler, IPostMessageService } from '../post-message';
import {
    AppIdentifiers,
    AreaType,
    INavigationChannel,
    IShowBetslipData,
    MessageBroker,
    ProductPackageToID,
    TriggerType,
    IHeartBeat,
    PaymentChannel,
    PlayerChannel,
    SessionChannel,
    ILogger,
    ActionID,
    IControlCenterFullSpecificParams,
    ElementType,
    SourceType,
    ControlCenterOpenSource,
} from '@sparkware/uc-sdk-core';
import { PendingAuthenticationService } from '../authentication/services/pending-authentication.service';
import { UserContextToken } from '../../injection-tokens';
import { IUserContext } from '../user-context/user-context-interface';
import { WebLoginService } from '../web-login/web.login.service';
import { ILoginOptions } from '../authentication/models';
import { IAuthenticationStorage } from '../authentication/models/';
import { CtaHandlerActionIds } from '../../models/enums/general-enums';
import { v4 as uuidv4 } from 'uuid';
import { LoaderManager } from '../../loaders/LoaderManager';
import { PendingService } from './pending.service';
import { ClientTracking } from '../tracking/models/clientTracking';
import { UkMigrationElasticEvents } from '../uk-migration/enums';
import { IControlCenterPayload } from '../action-handler/handlers/control-center-handler/IControlCenterPayload';
import { IActionHandlerFactory } from '../action-handler/models/IActionHandlerFactory';
import { PostLoginService } from '../post-login/post-login.service';
import { ViewInjector } from '../view-injector';

export class PendingHandler implements IPostMessageHandler {
    private readonly _logger: ILogger;
    private readonly _pendingService: PendingService;
    private readonly _viewInjector: ViewInjector;
    private readonly _paymentsChannel: PaymentChannel;
    private readonly _pendingAuthenticationService: PendingAuthenticationService;
    private readonly _playerChannel: PlayerChannel;
    private readonly _sessionChannel: SessionChannel;
    private readonly _userContext: IUserContext;
    private readonly _webLoginService: WebLoginService;
    private readonly _clientTracking: ClientTracking;
    private readonly _postLoginService: PostLoginService;

    public get name(): string {
        return 'PendingPostMessageHandler';
    }

    private get _authenticationStoragePromise(): Promise<IAuthenticationStorage> {
        return LoaderManager.Instance.AuthenticationStorageLoader.Instance;
    }

    private get _postMessageServicePromise(): Promise<IPostMessageService> {
        return LoaderManager.Instance.PostMessageServiceLoader.Instance;
    }

    private get _actionHandlerFactoryPromise(): Promise<IActionHandlerFactory> {
        return LoaderManager.Instance.ActionHandlerFactoryLoader.Instance;
    }

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger(this.name);
        this._pendingService = Container.get(PendingService);
        this._userContext = Container.get(UserContextToken);
        this._webLoginService = Container.get(WebLoginService);
        this._playerChannel = MessageBroker.getInstance().player;
        this._sessionChannel = MessageBroker.getInstance().session;
        this._paymentsChannel = MessageBroker.getInstance().payments;
        this._pendingAuthenticationService = Container.get(PendingAuthenticationService);
        this._viewInjector = Container.get(ViewInjector);
        this._clientTracking = Container.get(ClientTracking);
        this._postLoginService = Container.get(PostLoginService);
        this._tryRegisterPostMessageHandler();
    }

    public handler = (event: any): Promise<boolean> => this._handleMessages(event);

    private _tryRegisterPostMessageHandler = async (): Promise<void> => {
        this._logger.debug(`Registering ${this.name}...`);
        const postMessageService = await this._postMessageServicePromise;
        postMessageService?.addHandler(this);
    };

    private _handleMessages = async (event: MessageEvent): Promise<boolean> => {
        this._logger.debug(`Handler PendingPostMessageHandler is going to be executed...`);
        const { data } = event;

        if (typeof data !== 'object' || !data.ActionId) {
            return false;
        }

        const hasPendingActionId = [
            CtaHandlerActionIds.VerificationModule,
            CtaHandlerActionIds.Pending,
            CtaHandlerActionIds.QuickDeposit,
        ].some((actionId) => actionId === data.ActionId);
        const hasOnReady =
            data.ActionId == 'OnReady' &&
            data?.originInformation?.originProductPackageID == ProductPackageToID.PENDING;

        const hasOpenControlCenterFull = data.ActionId == CtaHandlerActionIds.ControlCentre;

        if (hasPendingActionId) {
            await this.onHasPendingActionId(data);
        } else if (hasOpenControlCenterFull) {
            await this.onOpenControlCenterFull(data);
        } else if (data.method === 'cashier') {
            await this.onCashier(data);
        } else if (data.method === 'lobby') {
            await this.onLobby();
        } else if (data.method === 'keepAlive') {
            await this.onKeepAlive();
        } else if (hasOnReady) {
            await this.onReadyPendingHandler(data);
        }

        return true;
    };

    private async onOpenControlCenterFull(
        data: IControlCenterPayload<IControlCenterFullSpecificParams>,
    ): Promise<void> {
        await this._viewInjector.CloseWrapDiv();
        const launchInfo = {
            businessCorrelationID: uuidv4(),
            sourceAppID: AppIdentifiers.UnifiedClient,
            sourceAppVersion: '0.0.0',
            channel: {
                area: data?.Channel?.area || AreaType.UD,
                element: data?.Channel?.element || ElementType.UD,
                source: data?.Channel?.source || SourceType.UnifiedClient,
            },
            appSpecificParameters: {
                clientPlatform: 1,
                source: ControlCenterOpenSource.PendingAction,
                ...data?.AppSpecificParameters,
            },
            trigger: TriggerType.userSelection,
        };
        const isAuthenticated = this._userContext.IsAuthenticated;
        if (isAuthenticated) {
            const actionHandlerFactory = await this._actionHandlerFactoryPromise;
            await actionHandlerFactory.performAction({
                actionID: ActionID.openControlCenterFull,
                correlationID: uuidv4(),
                launchInfo,
                actionData: {},
            });
        } else {
            let loginOptions: ILoginOptions = {
                postLogin: {
                    OpenControlCenterFull: {
                        enabled: true,
                        launchInfo,
                    },
                },
            };
            const authenticationStorage = await this._authenticationStoragePromise;
            authenticationStorage.updateLoginOptions(loginOptions);
            await this._pendingAuthenticationService.autoLogin(data);
        }
        const envelop = { headers: { publisher: 'PendingPostMessageHandler' }, topic: null };
        const trackingEvent = {
            dataString: JSON.stringify({ ...data, isAuthenticated }),
            event: UkMigrationElasticEvents.OpenControlCenterFull,
        };
        this._clientTracking.onSendEvent(trackingEvent, envelop);
    }

    private async onHasPendingActionId(data: {
        Token: string;
        ActionId: CtaHandlerActionIds;
    }): Promise<void> {
        const _currentPendingAction = this._pendingService.getCurrentAction();
        this._postLoginService.executeActions();
        if (data.Token) {
            _currentPendingAction?.resolve({
                closed: true,
                completed: true,
                error: null,
            });

            if (this._userContext.IsAuthenticated) {
                this._playerChannel.topics.showBetslip.publish({ publisher: this.name }, {
                    show: true,
                } as IShowBetslipData);
            } else {
                let loginOptions: ILoginOptions = null;
                if (data.ActionId === CtaHandlerActionIds.VerificationModule) {
                    loginOptions = {
                        postLogin: {
                            ShowKYC: {
                                enabled: true,
                            },
                        },
                    };
                }
                const authenticationStorage = await this._authenticationStoragePromise;
                authenticationStorage.updateLoginOptions(loginOptions);
                await this._pendingAuthenticationService.autoLogin(data);
            }
        } else {
            //close pending
            _currentPendingAction?.resolve({
                closed: true,
                completed: true,
                error: null,
            });
            await this._webLoginService.ResetLoginForm();
        }
    }

    private async onCashier(data: { method: any }): Promise<void> {
        const paymentAction: IPaymentAction = {
            Source: 'Pending action',
            Area: AreaType.PendingAction,
            Element: data.method,
        };
        this._paymentsChannel.topics.CTA_Cashier_Open.publish(
            { publisher: this.name },
            paymentAction,
        );
    }

    private async onLobby(): Promise<void> {
        await this._viewInjector.CloseWrapDiv();
        const _currentPendingAction = this._pendingService.getCurrentAction();
        _currentPendingAction?.resolve({
            closed: true,
            completed: false,
            error: null,
        });
    }

    private async onKeepAlive(): Promise<void> {
        this._sessionChannel.topics.heartBeat.publish({ publisher: this.name }, {} as IHeartBeat);
    }

    private async onReadyPendingHandler(data: { IsOk: boolean }): Promise<void> {
        let isOk = data.IsOk ?? false;
        const _currentPendingAction = this._pendingService.getCurrentAction();
        _currentPendingAction?.rendered(isOk);
    }
}
